import React from 'react';
import { i18n } from 'config/locales';
import Subsection from 'components/Section/shared/Subsection';
import connectSubsection from 'components/Section/shared/SubsectionConnector';
import { IDENTIFICATION, IDENTIFICATION_BIRTH_DATE } from 'config/formSections/identification';
import { FormErrorsUtil } from 'helpers/formErrors';
import { ff } from 'helpers/featureFlags';
import Field from 'components/Form/Field';
import DateControl from 'components/Form/DateControl';
import Show from 'components/Form/Show';
import Checkbox from 'components/Form/Checkbox';
import Textarea from 'components/Form/Textarea';
import { SUBSECTION_KEYS } from 'constants/sections';
import { getSafeDateTime } from 'helpers/date';
import { isPVQForm } from 'helpers/form';
import { IdentificationDateOfBirthModel } from 'forms/sections/identification/dateOfBirth/types';
import { FieldModel } from 'forms/shared/types';
import { TODAY } from '../../../../constants/dateLimits';

const sectionConfig = {
  key: IDENTIFICATION_BIRTH_DATE.key,
  section: IDENTIFICATION.name,
  store: IDENTIFICATION.store,
  subsection: IDENTIFICATION_BIRTH_DATE.name,
  storeKey: IDENTIFICATION_BIRTH_DATE.storeKey,
};

export class ApplicantBirthDate extends Subsection {
  constructor(props) {
    super(props);

    const {
      section, subsection, store, storeKey
    } = sectionConfig;

    this.section = section;
    this.subsection = subsection;
    this.store = store;
    this.storeKey = storeKey;

    this.state = {
      uid: `${subsection}-${super.guid()}`,
      errors: [],
      needsConfirmation: false,
    };

    this.update = this.update.bind(this);
    this.updateDate = this.updateDate.bind(this);
    this.updateConfirmed = this.updateConfirmed.bind(this);
    this.updateEstimatedDateOfBirthExplanation = this.updateEstimatedDateOfBirthExplanation.bind(this);
    this.handleError = this.handleError.bind(this);
    this.confirmationError = this.confirmationError.bind(this);
  }

  /**
   * Handle the change event.
   */
  update(queue) {
    this.props.onUpdate(
      this.storeKey,
      {
        Date: this.props.Date,
        Confirmed: this.props.Confirmed,
        EstimatedDateOfBirthExplanation: this.props.EstimatedDateOfBirthExplanation,
        ...queue,
      },

      // We add all keys as a good majority rely on ApplicantBirthDate for validation.
      SUBSECTION_KEYS
    );
  }

  updateDate = (values: IdentificationDateOfBirthModel) => {
    this.update({
      Date: values,
      EstimatedDateOfBirthExplanation: values.Date?.estimated ? this.props.EstimatedDateOfBirthExplanation : {} as FieldModel,
      Confirmed: { value: '', checked: false },
    });
  };

  updateEstimatedDateOfBirthExplanation(values) {
    this.update({
      EstimatedDateOfBirthExplanation: values,
    });
  }

  updateConfirmed(values) {
    this.update({
      Confirmed: values,
    });
  }

  handleError(value, arr) {
    let local = [...arr];

    // too old
    const hasMinError = local.some((x) => x.valid === false && x.code === 'date.min');

    // too young
    const hasMaxError = local.some((x) => x.valid === false && x.code === 'date.max');

    local.push({
      code: 'birthdate.age.max',
      valid: !hasMaxError,
      uid: this.state.uid,
    });

    local.push({
      code: 'birthdate.age.min',
      valid: !hasMinError,
      uid: this.state.uid,
    });

    local = local.filter((x) => x.code !== 'date.min' && x.code !== 'date.max');

    // Store the errors
    this.setState({ errors: local, needsConfirmation: hasMaxError });

    // Take the original and concatenate our new error values to it
    return super.handleError(value, local);
  }

  confirmationError(value) {
    let local = [...this.state.errors]; // eslint-disable-line
    local = local.filter((x) => x.code !== 'birthdate.age.max');
    local.push({
      code: 'birthdate.age.max',
      valid: value,
      uid: this.state.uid,
    });

    // Store the errors
    this.setState({ errors: local });

    // Take the original and concatenate our new error values to it
    return super.handleError(value, local);
  }

  render() {
    const klass = `section-content birthdate ${this.props.className || ''}`.trim();
    let requiresConfirmation = false;
    const isPVQ = isPVQForm(this.props.formType);

    // Only require confirmation if applicant is under the age of 16 and not born in the future
    if (this.props.Date) {
      const age = getSafeDateTime(this.props.Date);
      if (age.valid === true && TODAY.diff(age.datetime, 'years').years > 0.0) {
        requiresConfirmation = ff(this.props.features).getActive('EN-4733')
          ? FormErrorsUtil(this.props.formErrors).hasAttribute('Confirmed.checked')
          : this.state.needsConfirmation;
      }
    }

    return isPVQ ? (
      <div
        className={klass}
        data-section={IDENTIFICATION.key}
        data-subsection={IDENTIFICATION_BIRTH_DATE.key}
      >
        <h1 className="section-header">{i18n.t('identification.pvq.destination.birthdate')}</h1>
        <label className="birthdate-label">{i18n.m('identification.pvq.birthdate.info')}</label>
        <Field
          title={i18n.t('identification.pvq.birthdate.title')}
          titleSize="h4"
          help="identification.pvq.birthdate.help"
          scrollIntoView={this.props.scrollIntoView}
          attribute="Date"
          className="minimal-margin-bottom"
          exactAttribute
        >
          <DateControl
            name="birthdate"
            {...this.props.Date}
            relationship="Self"
            maxDate={TODAY}
            overrideError={(this.props.Confirmed || {}).checked}
            onUpdate={this.updateDate} // eslint-disable-line @typescript-eslint/unbound-method
            onError={this.handleError} // eslint-disable-line @typescript-eslint/unbound-method
            required={this.props.required}
            attribute="Date"
          />
        </Field>
        <Show when={this.props.Date?.estimated}>
          <Field
            title="Please explain."
            titleSize="label"
            scrollIntoView={this.props.scrollIntoView}
            attribute="EstimatedDateOfBirthExplanation"
          >
            <Textarea
              name="EstimatedDateOfBirthExplanation"
              className="estimated-dob-explanation"
              {...this.props.EstimatedDateOfBirthExplanation}
              onUpdate={this.updateEstimatedDateOfBirthExplanation}
              onError={this.handleError}
              required={this.props.required}
              attribute="EstimatedDateOfBirthExplanation"
            />
          </Field>
        </Show>
      </div>
    ) : (
      <div
        className={klass}
        data-section={IDENTIFICATION.key}
        data-subsection={IDENTIFICATION_BIRTH_DATE.key}
      >
        <h1 className="section-header">{i18n.t('identification.destination.birthdate')}</h1>
        <Field
          title={i18n.t('identification.birthdate.title')}
          titleSize="h4"
          help="identification.birthdate.help"
          scrollIntoView={this.props.scrollIntoView}
          attribute="Date"
        >
          <DateControl
            name="birthdate"
            {...this.props.Date}
            relationship="Self"
            maxDate={TODAY}
            overrideError={(this.props.Confirmed || {}).checked}
            onUpdate={this.updateDate} // eslint-disable-line @typescript-eslint/unbound-method
            onError={this.handleError} // eslint-disable-line @typescript-eslint/unbound-method
            required={this.props.required}
            attribute="Date"
          />

          <Show when={requiresConfirmation || (this.props.Confirmed?.checked === true ?? false)}>
            <Checkbox
              {...this.props.Confirmed}
              name="Confirmed"
              label={i18n.t('identification.birthdate.confirmation')}
              value="Confirmed"
              className="age-warning"
              onUpdate={this.updateConfirmed} // eslint-disable-line @typescript-eslint/unbound-method
              onError={this.confirmationError} // eslint-disable-line @typescript-eslint/unbound-method
              required={requiresConfirmation && this.props.required}
            />
          </Show>
        </Field>
      </div>
    );
  }
}

ApplicantBirthDate.defaultProps = {
  Date: {},
  Confirmed: {},
  EstimatedDateOfBirthExplanation: {},
  onUpdate: () => { },
  onError: (value, arr) => arr,
  dispatch: () => { },
};

export default connectSubsection(ApplicantBirthDate, sectionConfig);
